// src/components/ForwardModal.jsx
import { useState, useEffect } from "react";
import toast from "react-hot-toast";
import { axiosInstance } from "../lib/axios";
import { useChatStore } from "../store/useChatStore";
import { useAuthStore } from "../store/useAuthStore";

const getFilePath = (message) => {
  if (!message) return "";

  if (message.file?.savedAs) return `/files/${message.file.savedAs}`;
  if (message.file?.url) return message.file.url;
  if (typeof message.file === "string") return message.file;

  if (Array.isArray(message.files) && message.files.length > 0) {
    const firstFile = message.files[0];
    return firstFile.url || firstFile.path || "";
  }

  return "";
};

// Build the list of file references to forward (no download/re-upload).
const getFilesToForward = (message) => {
  if (!message) return [];

  // If the message carries a files array, reuse every entry
  if (Array.isArray(message.files) && message.files.length > 0) {
    return message.files
      .map((f) => ({
        url: f?.url || f?.path || "",
        fileName: f?.fileName || f?.name || "",
        fileSize: f?.fileSize || f?.size || 0,
        pageCount: f?.pageCount || "",
      }))
      .filter((f) => f.url);
  }

  // Single-file format (what your Message model stores)
  const filePath = getFilePath(message);
  if (!filePath) return [];

  return [
    {
      url: filePath,
      fileName:
        message.fileName ||
        message.file?.originalName ||
        message.file?.name ||
        filePath.split("/").pop() ||
        "forwarded_file",
      fileSize: message.fileSize || 0,
      pageCount: message.pageCount || "",
    },
  ];
};

const ForwardModal = ({ isOpen, onClose, message }) => {
  const { users, setSelectedUser, getMessages } = useChatStore();
  const authUser = useAuthStore((state) => state.authUser);

  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [forwarding, setForwarding] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setSelectedUserIds([]);
      setSearchTerm("");
      setForwarding(false);
    }
  }, [isOpen]);

  const toggleUser = (userId) => {
    setSelectedUserIds((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId],
    );
  };

  const handleForward = async () => {
    if (!selectedUserIds.length) {
      toast.error("Please select at least one user");
      return;
    }

    if (!message) {
      toast.error("No message selected");
      return;
    }

    const token = authUser?.token || localStorage.getItem("token");

    if (!token) {
      toast.error("No token found");
      return;
    }

    try {
      setForwarding(true);

      const text = message.text || message.content || "";
      const filesToForward = getFilesToForward(message);

      if (!text && filesToForward.length === 0) {
        toast.error("Nothing to forward");
        setForwarding(false);
        return;
      }

      for (const userId of selectedUserIds) {
        if (String(userId) === String(authUser?._id)) continue;

        // Text-only message
        if (filesToForward.length === 0) {
          const formData = new FormData();
          formData.append("text", text);

          await axiosInstance.post(`/messages/send/${userId}`, formData, {
            headers: { Authorization: `Bearer ${token}` },
          });
          continue;
        }

        // One message per file, using the single-file fields the backend saves
        for (let i = 0; i < filesToForward.length; i++) {
          const f = filesToForward[i];
          const formData = new FormData();

          // Text goes with the first file only
          if (text && i === 0) {
            formData.append("text", text);
          }

          formData.append("file", f.url);
          formData.append("fileName", f.fileName || "forwarded_file");
          formData.append("fileSize", String(f.fileSize || 0));

          if (f.pageCount) {
            formData.append("pageCount", String(f.pageCount));
          }

          await axiosInstance.post(`/messages/send/${userId}`, formData, {
            headers: { Authorization: `Bearer ${token}` },
          });
        }
      }

      toast.success("✅ Message forwarded successfully");

      if (selectedUserIds.length === 1) {
        const onlyUser = users.find((u) => u._id === selectedUserIds[0]);

        if (onlyUser) {
          setSelectedUser(onlyUser);
          await getMessages(onlyUser._id);
        }
      }

      onClose();
    } catch (err) {
      console.error("🚨 Forward error:", err);
      toast.error(err.response?.data?.message || "Failed to forward message");
    } finally {
      setForwarding(false);
    }
  };

  if (!isOpen) return null;

  const filteredUsers = users
    .filter((u) => String(u._id) !== String(authUser?._id))
    .filter((u) =>
      (u.name || "").toLowerCase().includes(searchTerm.toLowerCase().trim()),
    );

  return (
    <div style={overlayStyle}>
      <div style={modalStyle}>
        <h3 style={{ marginBottom: "10px" }}>Forward Message</h3>

        <input
          type="text"
          placeholder="Search users..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={searchStyle}
        />

        <div style={userListStyle}>
          {filteredUsers.map((user) => {
            const isSelected = selectedUserIds.includes(user._id);

            return (
              <div
                key={user._id}
                onClick={() => toggleUser(user._id)}
                style={{
                  ...userItemStyle,
                  backgroundColor: isSelected ? "#e0f0ff" : "#f9f9f9",
                  border: isSelected ? "2px solid #007bff" : "1px solid #ccc",
                }}
              >
                {isSelected ? "✅ " : ""}
                {user.name}
              </div>
            );
          })}

          {filteredUsers.length === 0 && (
            <div
              style={{ textAlign: "center", padding: "10px", color: "#999" }}
            >
              No users found
            </div>
          )}
        </div>

        <div style={footerStyle}>
          <button
            onClick={handleForward}
            disabled={!selectedUserIds.length || forwarding}
            style={{
              ...buttonStyle,
              backgroundColor:
                !selectedUserIds.length || forwarding ? "#999" : "#007bff",
            }}
          >
            {forwarding ? "Forwarding..." : "Forward"}
          </button>

          <button
            onClick={onClose}
            disabled={forwarding}
            style={{ ...buttonStyle, backgroundColor: "#ccc", color: "#000" }}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

const overlayStyle = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.5)",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  zIndex: 9999,
  padding: "15px",
};

const modalStyle = {
  background: "#fff",
  borderRadius: "10px",
  padding: "20px",
  width: "100%",
  maxWidth: "380px",
  maxHeight: "85vh",
  display: "flex",
  flexDirection: "column",
};

const searchStyle = {
  padding: "10px",
  marginBottom: "10px",
  borderRadius: "6px",
  border: "1px solid #ccc",
};

const userListStyle = {
  flex: 1,
  overflowY: "auto",
  border: "1px solid #ddd",
  borderRadius: "6px",
  padding: "6px",
  minHeight: "220px",
  maxHeight: "350px",
};

const userItemStyle = {
  padding: "10px",
  marginBottom: "8px",
  borderRadius: "6px",
  cursor: "pointer",
};

const footerStyle = {
  display: "flex",
  justifyContent: "space-between",
  gap: "10px",
  marginTop: "15px",
};

const buttonStyle = {
  flex: 1,
  padding: "10px 12px",
  color: "white",
  border: "none",
  borderRadius: "5px",
  cursor: "pointer",
};

export default ForwardModal;
